/** `compativel` só vem quando a consulta recebeu um material — sem ele, tudo é compatível. */
type ShadeOption = { id: string; system: string; code: string; compativel?: boolean };

export type ShadeSubgroup = { label: string; options: ShadeOption[] };
export type ShadeSystemGroup = { systemLabel: string; subgroups: ShadeSubgroup[] };

const SYSTEM_LABELS: Record<string, string> = {
  VITA_CLASSICAL: "VITA Classical",
  VITA_3D_MASTER: "VITA 3D-Master",
  CHROMASCOP: "Chromascop",
  OUTRO: "Outro",
};

const SYSTEM_ORDER = ["VITA_CLASSICAL", "VITA_3D_MASTER", "CHROMASCOP", "OUTRO"];

/** VITA Classical agrupa por letra ("A1".."A4" → "Tipo A"); 3D-Master agrupa pelo dígito de luminosidade ("2M2" → "Grupo 2"). */
function subgroupLabel(system: string, code: string): string {
  if (system === "VITA_CLASSICAL") return `Tipo ${code[0]}`;
  if (system === "VITA_3D_MASTER") return `Grupo ${code[0]}`;
  return "";
}

/** Classifica as cores por sistema (VITA Classical/3D-Master/...) e, dentro dele, por subgrupo (Tipo A, Grupo 2...). */
export function groupShadeOptions(shades: ShadeOption[]): ShadeSystemGroup[] {
  const bySystem = new Map<string, ShadeOption[]>();
  for (const s of shades) {
    if (!bySystem.has(s.system)) bySystem.set(s.system, []);
    bySystem.get(s.system)!.push(s);
  }

  // Escalas de fábrica primeiro, na ordem conhecida; escalas criadas pelo laboratório depois, em
  // ordem alfabética — antes o filtro pela lista fixa descartava as customizadas em silêncio.
  const customizados = [...bySystem.keys()]
    .filter((s) => !SYSTEM_ORDER.includes(s))
    .sort((a, b) => a.localeCompare(b, "pt-BR"));
  const ordem = [...SYSTEM_ORDER.filter((system) => bySystem.has(system)), ...customizados];

  return ordem.map((system) => {
    const options = bySystem.get(system)!;
    const bySubgroup = new Map<string, ShadeOption[]>();
    for (const opt of options) {
      const key = subgroupLabel(system, opt.code);
      if (!bySubgroup.has(key)) bySubgroup.set(key, []);
      bySubgroup.get(key)!.push(opt);
    }
    return {
      systemLabel: SYSTEM_LABELS[system] ?? system,
      subgroups: Array.from(bySubgroup.entries()).map(([label, opts]) => ({ label, options: opts })),
    };
  });
}
